import isEmpty from 'lodash/isEmpty';
import { EventConstants } from 'wcng-ui-common';

const {
  EventRegistrationTypeEnum: {
    REGISTRATION,
    EMAIL_VERIFICATION,
    SSO_REGISTRATION
  }
} = EventConstants;

/**
 * Determines the redirect destination for EventDefaultRedirect.
 *
 * Decision Matrix:
 * | regType          | user auth | preview | jwtToken | audienceToken | regMethod    | Result        |
 * |------------------|-----------|---------|----------|---------------|--------------|---------------|
 * | REGISTRATION     | no        | no      | no       | -             | -            | registration  |
 * | REGISTRATION     | yes       | no      | no       | -             | -            | presentation  |
 * | REGISTRATION     | any       | yes     | -        | -             | -            | presentation  |
 * | REGISTRATION     | any       | -       | yes      | -             | -            | presentation  |
 * | SSO              | any       | no      | no       | -             | -            | presentation  |
 * | TOKEN            | any       | no      | no       | -             | -            | presentation  |
 * | EMAIL_VERIFY     | no        | no      | no       | no            | -            | registration  |
 * | EMAIL_VERIFY     | any       | no      | no       | yes           | -            | presentation  |
 * | SSO_REGISTRATION | no        | no      | no       | -             | REGISTRATION | registration  |
 * | SSO_REGISTRATION | yes       | no      | no       | -             | REGISTRATION | presentation  |
 * | SSO_REGISTRATION | any       | no      | no       | -             | SSO          | presentation  |
 *
 * @param {Object} params
 * @param {string} params.registrationType - Event registration type
 * @param {Object|null} params.user - User object (null=unchecked, {}=unauthenticated, {...}=authenticated)
 * @param {boolean} params.preview - Preview mode flag
 * @param {string|null} params.jwtToken - JWT token from query params
 * @param {string|null} params.audienceToken - Audience token from query params
 * @param {string|null} params.regMethod - Registration method for SSO_REGISTRATION type
 * @returns {'registration'|'presentation'} - The destination path segment
 */
export const getEventRedirectDestination = ({
  registrationType,
  user,
  preview,
  jwtToken,
  audienceToken,
  regMethod
}) => {
  const isAuthenticated = user && !isEmpty(user);

  const showRegistrationUI =
    !preview &&
    !jwtToken &&
    !isAuthenticated && (
      registrationType === REGISTRATION ||
      (registrationType === EMAIL_VERIFICATION && !audienceToken) ||
      (registrationType === SSO_REGISTRATION && regMethod === 'REGISTRATION')
    );

  return showRegistrationUI ? 'registration' : 'presentation';
};

/**
 * Determines if an auth check is required before routing.
 *
 * @param {Object} params
 * @param {string} params.registrationType - Event registration type
 * @param {boolean} params.preview - Preview mode flag
 * @param {string|null} params.jwtToken - JWT token from query params
 * @param {string|null} params.audienceToken - Audience token from query params
 * @param {string|null} params.regMethod - Registration method for SSO_REGISTRATION type
 * @returns {boolean} - Whether an auth check is required
 */
export const requiresAuthCheck = ({
  registrationType,
  preview,
  jwtToken,
  audienceToken,
  regMethod
}) => {
  return (
    !preview &&
    !jwtToken &&
    !audienceToken &&
    (registrationType === REGISTRATION ||
     (registrationType === SSO_REGISTRATION && regMethod === 'REGISTRATION'))
  );
};
